import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { authHeaders, buildODataUrl, CustomGridToolbar, getODataRows, s, standardGridSx, Toast } from './listPageStyles';
import { DateInput } from './reports/DateInput';

const INVOICE_API = '/api/invoices';
const PAYMENT_API = '/api/payments';

const initialFilters = {
  vendorId: '',
  representativeId: '',
  customerId: '',
  paymentTypeId: '',
  startDate: '',
  endDate: '',
};

const moneyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function unwrapItems(payload) {
  return getODataRows(payload);
}

async function readJsonResponse(response, fallback = []) {
  if (!response.ok) return fallback;
  try {
    return await response.json();
  } catch {
    return fallback;
  }
}

function fetchWithTimeout(url, options = {}, timeoutMs = 15000) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => window.clearTimeout(timer));
}

function toDecimal(value) {
  const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function toDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString('tr-TR') : '-';
}

function formatMoney(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? moneyFormatter.format(amount) : moneyFormatter.format(0);
}

function normalizeGuid(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizePaymentTypeTextId(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replaceAll('/', '')
    .replaceAll('-', '')
    .replaceAll('_', '')
    .replaceAll(' ', '');
}

const paymentTypeCodeByTextId = {
  NAKIT: 1,
  VPOS: 2,
  HAVALE: 3,
  EFT: 3,
  CEK: 4,
  CHECK: 4,
  DBS: 5,
};

function resolvePaymentTypeCode(item) {
  const normalizedTextId = normalizePaymentTypeTextId(item?.textId);
  if (Object.prototype.hasOwnProperty.call(paymentTypeCodeByTextId, normalizedTextId)) {
    return paymentTypeCodeByTextId[normalizedTextId];
  }
  const parsed = Number.parseInt(item?.textId, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function makeLabel(item, fields) {
  if (!item) return '-';
  const code = item.textId || item.erpId || '';
  const name = fields.map(field => item[field]).find(Boolean) || '';
  return name ? `${code} - ${name}`.trim().replace(/^- /, '') : code || '-';
}

function sumRows(rows) {
  const totals = rows.reduce((acc, row) => ({
    debit: acc.debit + toDecimal(row.debit),
    receive: acc.receive + toDecimal(row.receive),
    balance: acc.balance + toDecimal(row.debit) - toDecimal(row.receive),
  }), { debit: 0, receive: 0, balance: 0 });

  return totals;
}

function summarizePaymentsByType(rows) {
  const totalsByType = new Map();

  rows
    .filter(row => row.sourceType === 'payment')
    .forEach(row => {
      const key = row.paymentTypeId || 'unknown';
      const current = totalsByType.get(key) || {
        id: key,
        label: row.paymentTypeLabel || 'Odeme Tipi Yok',
        amount: 0,
      };
      current.amount += toDecimal(row.receive);
      totalsByType.set(key, current);
    });

  return Array.from(totalsByType.values())
    .filter(item => item.amount !== 0)
    .sort((a, b) => a.label.localeCompare(b.label, 'tr'));
}

const styles = {
  shell: {
    ...s.container,
    gap: 14,
  },
  filterPanel: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 12,
    padding: 16,
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 8,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    minWidth: 0,
  },
  actions: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    flexWrap: 'wrap',
  },
  gridFrame: {
    background: 'var(--bg-card, #ffffff)',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid var(--border-color, #e5e7eb)',
  },
  gridWrap: {
    height: 530,
    minHeight: 420,
  },
  totalsGrid: {
    display: 'grid',
    gridTemplateColumns: '120px 140px minmax(150px, 1fr) minmax(140px, 1fr) 130px 140px 140px 150px minmax(190px, 1.2fr)',
    borderTop: '1px solid var(--border-color, #e5e7eb)',
    color: 'var(--text-primary, #111827)',
    minWidth: 1300,
    background: 'var(--bg-card, #ffffff)',
  },
  statementGridSx: {
    ...standardGridSx,
    '& .MuiDataGrid-footerContainer': { display: 'none' },
    '& .account-statement-row-invoice': {
      backgroundColor: 'rgba(251, 113, 133, 0.12)',
    },
    '& .account-statement-row-payment': {
      backgroundColor: 'rgba(74, 222, 128, 0.14)',
    },
    '& .account-statement-row-invoice:hover': {
      backgroundColor: 'rgba(251, 113, 133, 0.18)',
    },
    '& .account-statement-row-payment:hover': {
      backgroundColor: 'rgba(74, 222, 128, 0.2)',
    },
  },
  totalCell: {
    minHeight: 42,
    padding: '11px 10px',
    borderRight: '1px solid var(--border-color, #e5e7eb)',
    fontSize: 13,
    fontWeight: 800,
    textAlign: 'right',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  totalEmptyCell: {
    minHeight: 42,
    borderRight: '1px solid var(--border-color, #e5e7eb)',
  },
  paymentTypeName: {
    color: 'var(--text-secondary, #4b5563)',
    fontWeight: 800,
  },
  cumulativePanel: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 0.45fr) minmax(260px, 0.55fr)',
    gap: 16,
    padding: 16,
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 8,
  },
  summaryColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
    position: 'relative',
  },
  metric: {
    border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 8,
    padding: 14,
    background: 'var(--input-bg, #f8fafc)',
  },
  receivableMetric: {
    position: 'relative',
  },
  receivableConnector: {
    position: 'absolute',
    top: '50%',
    left: '100%',
    width: 28,
    borderTop: '2px solid var(--border-color, #cbd5e1)',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  metricLabel: {
    margin: 0,
    color: 'var(--text-secondary, #4b5563)',
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  metricValue: {
    margin: '6px 0 0',
    color: 'var(--text-primary, #111827)',
    fontSize: 20,
    fontWeight: 900,
  },
  paymentTypeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
  },
  paymentBreakdown: {
    position: 'relative',
    paddingLeft: 24,
    minWidth: 0,
  },
  paymentBraceLine: {
    position: 'absolute',
    top: 25,
    bottom: 25,
    left: 12,
    borderLeft: '2px solid var(--border-color, #cbd5e1)',
  },
  paymentTypeRow: {
    display: 'grid',
    gridTemplateColumns: '24px minmax(0, 1fr)',
    alignItems: 'center',
    marginLeft: -12,
    minWidth: 0,
  },
  paymentTypeConnector: {
    height: 0,
    borderTop: '2px solid var(--border-color, #cbd5e1)',
  },
  paymentTypeListItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 8,
    padding: 14,
    background: 'var(--input-bg, #f8fafc)',
    color: 'var(--text-primary, #111827)',
    fontSize: 14,
    fontWeight: 900,
  },
  paymentTypeEmpty: {
    border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 8,
    padding: 14,
    background: 'var(--input-bg, #f8fafc)',
    color: 'var(--text-secondary, #4b5563)',
    fontSize: 13,
    fontWeight: 700,
  },
};

export function AccountStatementPage() {
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const customerMap = useMemo(() => Object.fromEntries(customers.map(item => [normalizeGuid(item.id), item])), [customers]);
  const paymentTypeMap = useMemo(() => Object.fromEntries(paymentTypes.map(item => [normalizeGuid(item.id), item])), [paymentTypes]);

  const selectedPaymentTypeCode = useMemo(() => {
    if (!appliedFilters.paymentTypeId) return null;
    return resolvePaymentTypeCode(paymentTypeMap[normalizeGuid(appliedFilters.paymentTypeId)]);
  }, [appliedFilters.paymentTypeId, paymentTypeMap]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = authHeaders();
      const [invoiceRes, paymentRes, customerRes, representativeRes, vendorRes, paymentTypeRes] = await Promise.all([
        fetchWithTimeout(buildODataUrl(INVOICE_API, { pageSize: 5000, currentPage: 1, sortField: 'DocumentDate', sortDir: 'asc' }), { headers }),
        fetchWithTimeout(buildODataUrl(PAYMENT_API, { pageSize: 5000, currentPage: 1, sortField: 'PaymentDate', sortDir: 'asc' }), { headers }),
        fetchWithTimeout('/api/customers/Select', { headers }),
        fetchWithTimeout('/api/representatives/Select', { headers }),
        fetchWithTimeout('/api/vendors/Select', { headers }),
        fetchWithTimeout('/api/paymenttypes/Select', { headers }),
      ]);

      if (!invoiceRes.ok) throw new Error('Fatura bilgileri alinamadi');
      if (!paymentRes.ok) throw new Error('Odeme bilgileri alinamadi');

      const [invoicePayload, paymentPayload, customerPayload, representativePayload, vendorPayload, paymentTypePayload] = await Promise.all([
        readJsonResponse(invoiceRes),
        readJsonResponse(paymentRes),
        readJsonResponse(customerRes),
        readJsonResponse(representativeRes),
        readJsonResponse(vendorRes),
        readJsonResponse(paymentTypeRes),
      ]);

      setInvoices(unwrapItems(invoicePayload));
      setPayments(unwrapItems(paymentPayload));
      setCustomers(unwrapItems(customerPayload));
      setRepresentatives(unwrapItems(representativePayload));
      setVendors(unwrapItems(vendorPayload));
      setPaymentTypes(unwrapItems(paymentTypePayload).filter(item => item?.isActive !== false));
    } catch (error) {
      showToast('error', error.message || 'Ekstre verileri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const invoiceMap = useMemo(() => Object.fromEntries(invoices.map(item => [normalizeGuid(item.id), item])), [invoices]);

  const buildStatementRows = useCallback((includeDateFilter) => {
    const start = includeDateFilter ? toDate(appliedFilters.startDate) : null;
    const endRaw = includeDateFilter ? toDate(appliedFilters.endDate) : null;
    const end = endRaw ? new Date(endRaw.getFullYear(), endRaw.getMonth(), endRaw.getDate(), 23, 59, 59, 999) : null;

    const matchesInvoice = (invoice) => {
      if (appliedFilters.vendorId && normalizeGuid(invoice.vendorId) !== normalizeGuid(appliedFilters.vendorId)) return false;
      if (appliedFilters.representativeId && normalizeGuid(invoice.representativeId) !== normalizeGuid(appliedFilters.representativeId)) return false;
      if (appliedFilters.customerId && normalizeGuid(invoice.customerId) !== normalizeGuid(appliedFilters.customerId)) return false;
      if (selectedPaymentTypeCode != null && Number(invoice.paymentType) !== selectedPaymentTypeCode) return false;
      return true;
    };

    const inDateRange = (dateValue) => {
      const date = toDate(dateValue);
      if (!date) return false;
      if (start && date < start) return false;
      if (end && date > end) return false;
      return true;
    };

    const invoiceRows = invoices
      .filter(matchesInvoice)
      .filter(invoice => !includeDateFilter || inDateRange(invoice.documentDate || invoice.createdDate))
      .map(invoice => ({
        id: `invoice-${invoice.id}`,
        date: invoice.documentDate || invoice.createdDate,
        dueDate: invoice.dueDate,
        documentNo: invoice.printedNo || invoice.textId || '-',
        referenceNo: invoice.receiptNo || invoice.erpId || '-',
        documentType: 'Fatura',
        debit: toDecimal(invoice.netAmount),
        receive: 0,
        explanation: invoice.description1 || invoice.description2 || makeLabel(customerMap[normalizeGuid(invoice.customerId)], ['title', 'shortName', 'person']),
        sourceType: 'invoice',
      }));

    const paymentRows = payments
      .map(payment => {
        const invoice = payment.invoice || payment.Invoice || invoiceMap[normalizeGuid(payment.invoiceId || payment.InvoiceId)];
        return { payment, invoice };
      })
      .filter(({ invoice }) => invoice && matchesInvoice(invoice))
      .filter(({ payment }) => !appliedFilters.paymentTypeId || normalizeGuid(payment.paymentTypeId || payment.PaymentTypeId) === normalizeGuid(appliedFilters.paymentTypeId))
      .filter(({ payment }) => !includeDateFilter || inDateRange(payment.paymentDate || payment.PaymentDate || payment.createdDate))
      .map(({ payment, invoice }) => {
        const paymentTypeId = payment.paymentTypeId || payment.PaymentTypeId;
        const paymentType = payment.paymentType || payment.PaymentType || paymentTypeMap[normalizeGuid(paymentTypeId)];

        return {
          id: `payment-${payment.id || payment.Id}`,
          date: payment.paymentDate || payment.PaymentDate || payment.createdDate,
          dueDate: invoice?.dueDate,
          documentNo: payment.textId || payment.TextId || payment.receiptNo || '-',
          referenceNo: invoice?.printedNo || invoice?.textId || '-',
          documentType: 'Odeme',
          debit: 0,
          receive: toDecimal(payment.amount || payment.Amount),
          paymentTypeId,
          paymentTypeLabel: makeLabel(paymentType, ['description']),
          explanation: payment.description || payment.Description || payment.bankName || payment.BankName || 'Tahsilat',
          sourceType: 'payment',
        };
      });

    let runningBalance = 0;
    return [...invoiceRows, ...paymentRows]
      .sort((a, b) => {
        const left = toDate(a.date)?.getTime() ?? 0;
        const right = toDate(b.date)?.getTime() ?? 0;
        if (left !== right) return left - right;
        return a.sourceType.localeCompare(b.sourceType);
      })
      .map((row, index) => {
        runningBalance += toDecimal(row.debit) - toDecimal(row.receive);
        return { ...row, lineNo: index + 1, balance: runningBalance };
      });
  }, [appliedFilters, customerMap, invoiceMap, invoices, payments, paymentTypeMap, selectedPaymentTypeCode]);

  const periodRows = useMemo(() => buildStatementRows(true), [buildStatementRows]);
  const accountRows = useMemo(() => buildStatementRows(false), [buildStatementRows]);
  const periodTotals = useMemo(() => sumRows(periodRows), [periodRows]);
  const accountTotals = useMemo(() => sumRows(accountRows), [accountRows]);
  const paymentTypeTotals = useMemo(() => summarizePaymentsByType(periodRows), [periodRows]);

  const columns = [
    { field: 'date', headerName: 'Tarih', width: 120, renderCell: params => formatDate(params.row.date) },
    { field: 'dueDate', headerName: 'Net Vade Tarihi', width: 140, renderCell: params => formatDate(params.row.dueDate) },
    { field: 'documentNo', headerName: 'Dokuman No', minWidth: 150, flex: 1 },
    { field: 'referenceNo', headerName: 'Referans No', minWidth: 140, flex: 1 },
    { field: 'documentType', headerName: 'Dokuman Tipi', width: 130 },
    { field: 'debit', headerName: 'Borc', width: 140, align: 'right', headerAlign: 'right', renderCell: params => formatMoney(params.row.debit) },
    { field: 'receive', headerName: 'Alacak', width: 140, align: 'right', headerAlign: 'right', renderCell: params => formatMoney(params.row.receive) },
    { field: 'balance', headerName: 'Bakiye', width: 150, align: 'right', headerAlign: 'right', renderCell: params => formatMoney(params.row.balance) },
    { field: 'explanation', headerName: 'Aciklama', minWidth: 190, flex: 1.2 },
  ];

  const setFilter = (key, value) => setFilters(current => ({ ...current, [key]: value }));

  const renderSelect = (label, key, items, fields) => (
    <label style={styles.field}>
      <span style={s.label}>{label}</span>
      <select style={s.select} value={filters[key]} onChange={event => setFilter(key, event.target.value)}>
        <option value="">Tum kayitlar</option>
        {items.map(item => (
          <option key={item.id} value={item.id}>{makeLabel(item, fields)}</option>
        ))}
      </select>
    </label>
  );

  const periodFooter = (
    <>
      <div style={styles.totalEmptyCell} />
      <div style={styles.totalEmptyCell} />
      <div style={styles.totalEmptyCell} />
      <div style={styles.totalEmptyCell} />
      <div style={styles.totalEmptyCell} />
      <div style={styles.totalCell}>{formatMoney(periodTotals.debit)}</div>
      <div style={styles.totalCell}>{formatMoney(periodTotals.receive)}</div>
      <div style={styles.totalEmptyCell} />
      <div style={{ ...styles.totalEmptyCell, borderRight: 0 }} />
    </>
  );

  return (
    <div style={styles.shell}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Hesap Ekstresi Takip</h2>
          <p style={s.pageSubtitle}>Fatura ve odeme hareketlerini tek ekstre akisi olarak izleyin.</p>
        </div>
        <button style={s.btnSecondary} onClick={loadData}>Yenile</button>
      </div>

      <div style={styles.filterPanel}>
        {renderSelect('Distribütör', 'vendorId', vendors, ['name'])}
        {renderSelect('Bolge / Temsilci', 'representativeId', representatives, ['name'])}
        {renderSelect('Bayi / Musteri', 'customerId', customers, ['title', 'shortName', 'person'])}
        {renderSelect('Odeme Tipi', 'paymentTypeId', paymentTypes, ['description'])}
        <label style={styles.field}>
          <span style={s.label}>Baslangic Tarihi</span>
          <DateInput value={filters.startDate} onChange={(v) => setFilter('startDate', v)} />
        </label>
        <label style={styles.field}>
          <span style={s.label}>Bitis Tarihi</span>
          <DateInput value={filters.endDate} onChange={(v) => setFilter('endDate', v)} />
        </label>
        <div style={styles.actions}>
          <button style={s.btnPrimary} onClick={() => setAppliedFilters(filters)}>Ara</button>
          <button style={s.btnSecondary} onClick={() => { setFilters(initialFilters); setAppliedFilters(initialFilters); }}>Temizle</button>
        </div>
      </div>

      <div style={styles.gridFrame}>
        <div style={styles.gridWrap}>
          <DataGrid
            rows={periodRows}
            columns={columns}
            loading={loading}
            getRowId={row => row.id}
            rowHeight={42}
            columnHeaderHeight={46}
            hideFooter
            showToolbar
            slots={{ toolbar: CustomGridToolbar }}
            slotProps={{ toolbar: { fileName: 'hesap-ekstresi' } }}
            disableRowSelectionOnClick
            localeText={{ noRowsLabel: 'Ekstre hareketi bulunamadi' }}
            getRowClassName={params => params.row.sourceType === 'payment' ? 'account-statement-row-payment' : 'account-statement-row-invoice'}
            sx={styles.statementGridSx}
          />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <div style={styles.totalsGrid}>
            {periodFooter}
          </div>
        </div>
      </div>

      <div style={styles.cumulativePanel}>
        <div style={styles.summaryColumn}>
          <div style={styles.metric}>
            <p style={styles.metricLabel}>Toplam Borc</p>
            <p style={styles.metricValue}>{formatMoney(accountTotals.debit)}</p>
          </div>
          <div style={{ ...styles.metric, ...styles.receivableMetric }}>
            <p style={styles.metricLabel}>Toplam Alacak</p>
            <p style={styles.metricValue}>{formatMoney(accountTotals.receive)}</p>
            <span style={styles.receivableConnector} aria-hidden="true" />
          </div>
          <div style={styles.metric}>
            <p style={styles.metricLabel}>Toplam Bakiye</p>
            <p style={styles.metricValue}>{formatMoney(accountTotals.balance)}</p>
          </div>
        </div>
        <div style={styles.paymentBreakdown}>
          {paymentTypeTotals.length > 0 && <div style={styles.paymentBraceLine} aria-hidden="true" />}
          <div style={styles.paymentTypeList}>
            {paymentTypeTotals.length === 0 ? (
              <div style={styles.paymentTypeEmpty}>Odeme tipi toplamı bulunamadi.</div>
            ) : paymentTypeTotals.map(item => (
              <div key={`bottom-${item.id}`} style={styles.paymentTypeRow}>
                <span style={styles.paymentTypeConnector} aria-hidden="true" />
                <div style={styles.paymentTypeListItem}>
                  <span style={styles.paymentTypeName}>{item.label}</span>
                  <span>{formatMoney(item.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Toast toast={toast} />
    </div>
  );
}
